import React from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import SkinCarousel from './SkinCarousel';

interface Feature {
  label: string;
  title: string;
  description: string;
}

interface CarouselProps {
  features: Feature[];
  featureIndex: number;
  progress: number;
  handleManualSwitch: (index: number) => void;
}

const Carousel = ({features, featureIndex, progress, handleManualSwitch}: CarouselProps) => {
  const {t} = useTranslation();

  return (
      <section className="relative w-full max-w-[1600px] mx-auto px-6 lg:px-12 pt-8 flex flex-col md:flex-row items-start gap-0 md:gap-16">
        <div className="flex-1 z-10 w-full lg:pt-12 pt-0">
          {/* Main container with fixed height to prevent overall layout jumps */}
          <div className="relative min-h-[520px] flex flex-col">

            {/* Wrapper for the text content with a fixed height to keep the button position stable */}
            <div className="h-[370px]">
              <AnimatePresence mode="wait">
                <motion.div
                    key={featureIndex}
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                    exit={{opacity: 0, y: -10}}
                    transition={{duration: 0.4}}
                    className="flex flex-col"
                >
                  <span className="text-dark-primary font-bold tracking-[0.3em] text-sm mb-6 block uppercase">
                    {features[featureIndex].label}
                  </span>

                  <h1 className="text-4xl md:text-5xl font-black leading-tight mb-8 text-light-text dark:text-dark-text">
                    {features[featureIndex].title}
                  </h1>

                  <p className="text-2xl text-gray-800 dark:text-slate-400 max-w-xl leading-relaxed">
                    {features[featureIndex].description}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Button and Navigation area - now always starts at the same Y-position */}
            <div className="flex flex-col">
              <Link to="/market"
                    className="bg-dark-primary hover:bg-dark-stronger text-white px-12 py-5 rounded-2xl font-bold text-xl transition-all shadow-lg shadow-blue-900/10 inline-block w-fit">
                {t('landing_page.carousel.explore_market')}
              </Link>

              <div className="mt-8 flex flex-col gap-6 w-full max-w-md">
                <div className="flex items-center gap-4 mb-2 text-light-text dark:text-dark-text">
                  <button onClick={() => handleManualSwitch((featureIndex - 1 + features.length) % features.length)}
                          className="hover:text-dark-primary transition-colors">&lt;</button>
                  <span className="text-sm font-mono tracking-widest">{featureIndex + 1} / {features.length}</span>
                  <button onClick={() => handleManualSwitch((featureIndex + 1) % features.length)}
                          className="hover:text-dark-primary transition-colors">&gt;</button>
                </div>
                <div className="flex gap-3">
                  {features.map((_, i) => (
                      <button key={i} onClick={() => handleManualSwitch(i)} className="h-1.5 flex-1 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden relative">
                        {i === featureIndex && (
                            <motion.div className="absolute left-0 top-0 h-full bg-dark-primary shadow-[0_0_10px_rgba(59,130,246,0.5)]"
                                        style={{width: `${progress}%`}}/>
                        )}
                        {i < featureIndex && <div className="absolute left-0 top-0 h-full w-full bg-gray-400 dark:bg-slate-600"/>}
                      </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 w-full">
          <SkinCarousel />
        </div>
      </section>
  );
};

export default Carousel;